import * as Sentry from "@sentry/react";

/**
 * Forwards browser Content-Security-Policy violations to Sentry.
 *
 * The browser fires a `securitypolicyviolation` event (and logs to the console)
 * whenever a resource is blocked or — while we roll out report-only — would be
 * blocked. This listener mirrors those events into Sentry so we can see missing
 * allowlist entries aggregated across deployed environments, not just in a single
 * developer's console. See apps/luna-locate/csp.config.js for the policy itself.
 *
 * Note: report-only policies can emit many violations per page load, so
 * duplicates (same directive + blocked origin) are throttled to keep Sentry
 * signal clean. Reporting is a no-op locally because Sentry is disabled when
 * NX_ENVIRONMENT === "local" (see main.tsx).
 */

// Remember (directive + blocked origin) pairs already reported this session.
const reported = new Set<string>();

const blockedOrigin = (blockedURI: string): string => {
  try {
    return new URL(blockedURI).origin;
  } catch {
    // Keywords like "inline", "eval", "data" aren't URLs — report them as-is.
    return blockedURI;
  }
};

export const handleCspViolation = (event: SecurityPolicyViolationEvent): void => {
  const dedupeKey = `${event.effectiveDirective}|${blockedOrigin(event.blockedURI)}`;
  if (reported.has(dedupeKey)) {
    return;
  }
  reported.add(dedupeKey);

  Sentry.captureMessage(`CSP violation: ${event.effectiveDirective}`, {
    level: "warning",
    tags: {
      csp_directive: event.effectiveDirective,
      csp_disposition: event.disposition, // "report" (report-only) or "enforce"
      csp_blocked_origin: blockedOrigin(event.blockedURI)
    },
    extra: {
      blockedURI: event.blockedURI,
      violatedDirective: event.violatedDirective,
      originalPolicy: event.originalPolicy,
      documentURI: event.documentURI,
      sourceFile: event.sourceFile,
      lineNumber: event.lineNumber,
      columnNumber: event.columnNumber
    }
  });
};

/**
 * Registers the CSP violation listener. Returns a cleanup function that removes it.
 */
export const reportCspViolationsToSentry = (): (() => void) => {
  document.addEventListener("securitypolicyviolation", handleCspViolation);
  return () => document.removeEventListener("securitypolicyviolation", handleCspViolation);
};
